import { CameraOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { Alert, Button, Empty, Flex, Space, Spin, Tag, Typography } from 'antd';
import { type ReactNode, useRef } from 'react';
import {
  type FloorplanDimensions,
  formatPlanCoordinate,
  type PlanPoint,
} from '@/features/inventorization/position/floorplan-marker-geometry';
import { FloorplanMarkerPicker } from '@/routers/inventorization/components/position/FloorplanMarkerPicker';
import { OtherMetersMapSection } from '@/routers/inventorization/components/position/OtherMetersMapSection';
import { FLOORPLAN_BLOB_SOURCES, type FloorplanBlobSource } from '@/shared/db/schema';
import { translateInventoryText } from '@/shared/i18n/inventory-i18n';

export function FloorplanPlacementPanel({
  allowLocalFloorplanReplace = false,
  alertsClosable = false,
  emptyDescription,
  existingPins,
  floorplanDimensions,
  floorplanImageUrl,
  floorplanName,
  floorplanRecord,
  imageDescription,
  isLoading,
  isSavingFloorplan,
  loadingContent,
  loadingMinHeight = 180,
  onPickMarker,
  onUpload,
  selectedMarker,
  showPinSummary = true,
  uploadError,
}: {
  allowLocalFloorplanReplace?: boolean;
  alertsClosable?: boolean;
  emptyDescription: string;
  existingPins: PlanPoint[];
  floorplanDimensions: FloorplanDimensions | null;
  floorplanImageUrl: string | null;
  floorplanName?: string;
  floorplanRecord?: { name?: string; pendingUpload?: boolean; source?: FloorplanBlobSource } | null;
  imageDescription?: string;
  isLoading: boolean;
  isSavingFloorplan: boolean;
  loadingContent?: ReactNode;
  loadingMinHeight?: number;
  onPickMarker: (point: PlanPoint) => void;
  onUpload: (file: File | null) => void;
  selectedMarker: PlanPoint | null;
  showPinSummary?: boolean;
  uploadError: string | null;
}) {
  if (isLoading) {
    return (
      <Flex align="center" justify="center" style={{ minHeight: loadingMinHeight }}>
        {loadingContent ?? <Spin />}
      </Flex>
    );
  }

  if (floorplanImageUrl) {
    return (
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {floorplanName ? <Typography.Text strong>{floorplanName}</Typography.Text> : null}
        {imageDescription ? (
          <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
            {imageDescription}
          </Typography.Paragraph>
        ) : null}
        {floorplanRecord?.pendingUpload ? (
          <Alert
            closable={alertsClosable}
            message={translateInventoryText(
              'app.inventory.position.floorplan.savedLocally',
              'Floorplan saved locally and pending upload.',
            )}
            showIcon
            type="info"
          />
        ) : null}
        {allowLocalFloorplanReplace && isLocalFloorplanRecord(floorplanRecord) ? (
          <PickFloorplanButton isSaving={isSavingFloorplan} onUpload={onUpload} />
        ) : null}
        <OtherMetersMapSection
          count={existingPins.length}
          countDefaultMessage="{count} floor pins"
          countMessageId="app.inventory.position.floorplan.floorPins"
          renderMap={showOtherMeters => (
            <FloorplanMarkerPicker
              existingPins={showOtherMeters ? existingPins : []}
              floorplanDimensions={floorplanDimensions}
              imageUrl={floorplanImageUrl}
              onPick={onPickMarker}
              selectedMarker={selectedMarker}
            />
          )}
        />
        {showPinSummary && selectedMarker ? (
          <Flex gap="small" wrap>
            <FloorplanMarkerSummary selectedMarker={selectedMarker} />
          </Flex>
        ) : null}
      </Space>
    );
  }

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Empty description={emptyDescription} image={Empty.PRESENTED_IMAGE_SIMPLE} />
      <PickFloorplanButton isSaving={isSavingFloorplan} onUpload={onUpload} />
      {uploadError ? <Alert closable={alertsClosable} message={uploadError} showIcon type="error" /> : null}
    </Space>
  );
}

export function FloorplanPinSummary({
  existingPins,
  selectedMarker,
}: {
  existingPins: PlanPoint[];
  selectedMarker: PlanPoint | null;
}) {
  return (
    <>
      <Tag icon={<EnvironmentOutlined />}>
        {translateInventoryText('app.inventory.position.floorplan.floorPins', '{count} floor pins', {
          count: existingPins.length,
        })}
      </Tag>
      {selectedMarker ? <FloorplanMarkerSummary selectedMarker={selectedMarker} /> : null}
    </>
  );
}

function FloorplanMarkerSummary({ selectedMarker }: { selectedMarker: PlanPoint }) {
  return (
    <Tag color="blue">
      x {formatPlanCoordinate(selectedMarker.x)}, y {formatPlanCoordinate(selectedMarker.y)}
    </Tag>
  );
}

function isLocalFloorplanRecord(
  floorplanRecord: { pendingUpload?: boolean; source?: FloorplanBlobSource } | null | undefined,
) {
  return floorplanRecord?.source === FLOORPLAN_BLOB_SOURCES.local || floorplanRecord?.pendingUpload === true;
}

function PickFloorplanButton({ isSaving, onUpload }: { isSaving: boolean; onUpload: (file: File | null) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <>
      <Button
        icon={<CameraOutlined />}
        loading={isSaving}
        onClick={() => inputRef.current?.click()}
        size="large"
        type="default"
      >
        {translateInventoryText('app.inventory.position.actions.pickFloorplan', 'Pick floorplan')}
      </Button>
      <input
        accept="image/*"
        hidden
        onChange={event => {
          const file = event.currentTarget.files?.[0] ?? null;
          event.currentTarget.value = '';
          onUpload(file);
        }}
        ref={inputRef}
        type="file"
      />
    </>
  );
}
